/**
 * Wind down (decision 42): the last few rounds of the day, drawn so that everyone here ends on
 * the same number of games.
 *
 * The sum. Everyone here (status "in") has played g games. The day ends level on T games when
 * the further rounds give each of them T - g more. T starts at the most games anyone here has;
 * the further games add up to D = the sum of T - g, and since every court holds four, D has to
 * divide by 4. Each round holds a multiple of four, at least four and at most four a court, and
 * nobody plays twice in a round, so nobody can need more games than there are rounds. When T
 * cannot work it goes up by one, which gives everyone here a game more. That can cost a lot: with
 * an odd number here, D only divides by 4 for one T in four. So when it saves rounds the admin
 * is also offered the near sum: everyone ends on T except up to three, who end one game short.
 * Which three is whichever takes fewest rounds: those already closest to T (who then sit the
 * last rounds out), or those furthest short, usually someone who arrived late and would otherwise
 * hold the hall on one court for round after round; then whoever has played most for the time
 * they have been here. With some numbers here no T at all is level (32 here whose games add up to
 * an odd number, after someone left): then only the near sum is offered.
 *
 * The draw (scheduler.ts) takes the round's number of players from the plan and picks those
 * still furthest short, most short first: the standard way to fill the rounds so it always comes
 * out (Gale and Ryser). The plan is worked out again after every event and kept as it was while
 * it still adds up, so the same people stay short. When it stops adding up (someone arrives,
 * leaves, goes on a break or comes back, or the courts change) it is worked out afresh the way the
 * admin chose it. A level sum stays level while level can be done with those here, even when that
 * takes more rounds; it takes the near sum only when level cannot be done, and the card says so.
 * A near sum is worked out afresh as the quickest, level when that is as quick: a return from a
 * break must not push everyone two games further for one person.
 *
 * Pure: nothing here reads a clock or a random source.
 */

import { availablePlayers, history } from "./selectors";
import type { PlayerId, TournamentState, WindDown } from "./types";

/** The most T goes above the most games anyone here has: every remainder of D by 4 is covered by then. */
const MAX_RAISE = 3;
/** The most who may end one game short in the near sum: any more and four of them could play one more round. */
const MAX_SHORT = 3;

export interface WindDownPlan {
  /** Everyone here ends on this many games. */
  target: number;
  /** Except these, who end one game short of it. Empty in the level sum. */
  short: PlayerId[];
  /** How many play in each further round, in order: 16 is four courts, 8 is two. Empty when it is done. */
  rounds: number[];
  /** Further games each player here still needs. */
  need: Record<PlayerId, number>;
  /** How many are here. */
  here: number;
}

/**
 * The round sizes that give each player their further games, front-loaded (full courts first),
 * or null. `demands` are the further games of those who still need any.
 */
function sizesFor(demands: number[], rounds: number, cap: number): number[] | null {
  const total = demands.reduce((a, b) => a + b, 0);
  const found: number[] = [];
  // Ryser's greedy: each round takes the players with the most still to play. It finds a way if
  // there is one, so only the multiset of round sizes needs searching.
  const works = (sizes: number[]): boolean => {
    const d = [...demands];
    for (let r = 0; r < sizes.length; r += 1) {
      d.sort((x, y) => y - x);
      const size = sizes[r] as number;
      if ((d[0] ?? 0) > sizes.length - r) return false;
      if ((d[size - 1] ?? 0) < 1) return false;
      for (let i = 0; i < size; i += 1) d[i] = (d[i] as number) - 1;
    }
    return d.every((x) => x === 0);
  };
  const search = (left: number, sum: number, largest: number): boolean => {
    if (left === 0) return sum === 0 && works(found);
    for (let size = Math.min(largest, sum - 4 * (left - 1)); size >= 4; size -= 4) {
      if (size * left < sum) break;
      found.push(size);
      if (search(left - 1, sum - size, size)) return true;
      found.pop();
    }
    return false;
  };
  return search(rounds, total, cap) ? [...found] : null;
}

/** The fewest further rounds that give each player exactly their further games, or null. */
function fewestRounds(need: Record<PlayerId, number>, cap: number): number[] | null {
  const demands = Object.values(need).filter((d) => d > 0);
  const total = demands.reduce((a, b) => a + b, 0);
  if (total === 0) return [];
  if (total % 4 !== 0 || cap < 4 || demands.length < 4) return null;
  const most = Math.max(...demands);
  for (let rounds = Math.max(most, Math.ceil(total / cap)); rounds * 4 <= total; rounds += 1) {
    const sizes = sizesFor(demands, rounds, cap);
    if (sizes) return sizes;
  }
  return null;
}

function capOf(state: TournamentState, here: number): number {
  return Math.min(state.config.courts, Math.floor(here / state.config.playersPerCourt)) * state.config.playersPerCourt;
}

/** The plan for a given T and the ones ending one short, or null when it does not add up. */
function planFor(state: TournamentState, target: number, short: PlayerId[]): WindDownPlan | null {
  const here = availablePlayers(state);
  const games = history(state).gamesPlayed;
  const need: Record<PlayerId, number> = {};
  for (const id of here) {
    need[id] = target - (games[id] ?? 0) - (short.includes(id) ? 1 : 0);
    if ((need[id] as number) < 0) return null;
  }
  const rounds = fewestRounds(need, capOf(state, here.length));
  return rounds ? { target, short: short.filter((id) => here.includes(id)), rounds, need, here: here.length } : null;
}

/** Fewer rounds first, then fewer left short, then the lower T. */
function better(a: WindDownPlan | null, b: WindDownPlan | null): WindDownPlan | null {
  if (!a) return b;
  if (!b) return a;
  if (a.rounds.length !== b.rounds.length) return a.rounds.length < b.rounds.length ? a : b;
  if (a.short.length !== b.short.length) return a.short.length < b.short.length ? a : b;
  return a.target <= b.target ? a : b;
}

/**
 * A fresh plan from the state as it is. `near` false: everyone level, null when no T up to three
 * above the most anyone has can do it. `near` true: the fewest rounds with up to three ending one
 * game short; it is the level sum whenever that is as quick.
 */
export function planWindDown(state: TournamentState, near: boolean): WindDownPlan | null {
  const here = availablePlayers(state);
  if (!here.length) return null;
  const games = history(state).gamesPlayed;
  const g = (id: PlayerId): number => games[id] ?? 0;
  const rounds = history(state).roundsPresent;
  const share = (id: PlayerId): number => g(id) / Math.max(1, rounds[id] ?? 0);
  const top = Math.max(...here.map(g));
  let best: WindDownPlan | null = null;
  for (let target = top; target <= top + MAX_RAISE; target += 1) {
    best = better(best, planFor(state, target, []));
    if (!near) continue;
    // those already closest to T end one short (they play one fewer of the last rounds), or those
    // furthest short, when they are the ones holding the sum to more rounds
    const short = here.filter((id) => target - g(id) >= 1);
    const byShare = (x: PlayerId, y: PlayerId): number => share(y) - share(x) || (x < y ? -1 : 1);
    const closest = [...short].sort((x, y) => (target - g(x)) - (target - g(y)) || byShare(x, y));
    const furthest = [...short].sort((x, y) => (target - g(y)) - (target - g(x)) || byShare(x, y));
    for (let n = 1; n <= MAX_SHORT && n <= short.length; n += 1) {
      best = better(best, planFor(state, target, closest.slice(0, n)));
      best = better(best, planFor(state, target, furthest.slice(0, n)));
    }
  }
  return best;
}

/** What the preview offers: the level sum, and the near sum only when it takes fewer rounds (or level cannot be done). */
export function windDownChoices(state: TournamentState): { level: WindDownPlan | null; near: WindDownPlan | null } {
  const level = planWindDown(state, false);
  const near = planWindDown(state, true);
  const quicker = near && (!level || near.rounds.length < level.rounds.length) ? near : null;
  return { level, near: quicker };
}

/**
 * A fresh sum for a wind down that is on, as the admin chose it: everyone level for the level
 * sum, and the near sum only when level cannot be done with those here; the quickest for the near.
 */
function freshSum(state: TournamentState, near: boolean): WindDownPlan | null {
  return near ? planWindDown(state, true) : (planWindDown(state, false) ?? planWindDown(state, true));
}

/** The wind down as kept in the state: its sum, or a fresh one when it no longer adds up. Null when off or impossible. */
export function windDownNow(state: TournamentState): WindDownPlan | null {
  const on = state.windDown;
  if (!on) return null;
  return planFor(state, on.target, on.short) ?? freshSum(state, on.near);
}

/** True while a wind down is on. */
export function windingDown(state: TournamentState): boolean {
  return Boolean(state.windDown);
}

/** True once a wind down has played its last round: no more rounds are drawn. */
export function gamesDone(state: TournamentState): boolean {
  const plan = windDownNow(state);
  return Boolean(plan && plan.rounds.length === 0);
}

/**
 * The wind down as the reducer keeps it after an event: the same sum while it still adds up, so
 * the names left one short do not change from round to round; a fresh one when it does not. A
 * level sum the admin chose stays level whenever level can be done: it falls back to the near sum
 * only when nobody here can be evened out, and goes back to level as soon as that can be done.
 */
export function restamp(state: TournamentState): WindDown | null {
  const on = state.windDown;
  if (!on) return null;
  if (!on.near && on.short.length) {
    const level = planWindDown(state, false);
    if (level) return { ...on, target: level.target, short: [] };
  }
  if (planFor(state, on.target, on.short)) return on;
  const plan = freshSum(state, on.near);
  if (!plan) return on;
  return { ...on, target: plan.target, short: plan.short };
}
